import { useCallback, useEffect, useState } from "react";
import {
  centsFromInput,
  formatMoney,
  MAX_RECEIPT_LINES,
  todayLocal,
  type Receipt,
  type ReceiptLineInput,
  type ReserveTag,
  type StockRow,
  type StockTracking,
  formatCount,
} from "@iltocat/shared";
import { ApiError, api } from "../lib/api.js";
import { calendarDay } from "../lib/stockFormat.js";
import type { Taxonomy } from "../lib/useTaxonomy.js";
import { EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { Notice } from "../components/Notice.js";
import { PageHeader } from "../components/PageHeader.js";
import { Panel } from "../components/Panel.js";
import { Segmented } from "@vcnet/ui";
import { ReserveTagPicker } from "../components/ReserveTags.js";
import { hasCamera } from "../components/CameraView.js";
import { ScanSerialDialog } from "../components/ScanSerialDialog.js";
import { AttachmentLink } from "../components/StockPanel.js";
import { useToast } from "../components/Toast.js";

interface LineDraft {
  key: number;
  tracking: StockTracking;
  title: string;
  quantity: string;
  cost: string;
  categoryId: string;
  /** Serialized: one per line of the box, in order. */
  serials: string;
  /** Bulk: answered yes to "top up a line you already have?". */
  topUp: boolean;
  /** Bulk: the line to top up, once picked; "" for a new one or none picked yet. */
  existingItemId: string;
  /** Reserve tags every unit on the line wears, picked rather than typed. */
  tags: string[];
}

let nextKey = 0;
const blankLine = (tracking: StockTracking = "serialized"): LineDraft => ({
  key: (nextKey += 1),
  tracking,
  title: "",
  quantity: "1",
  cost: "",
  categoryId: "",
  serials: "",
  topUp: false,
  existingItemId: "",
  tags: [],
});

/** One serial per line, blank lines dropped -- the shape a barcode scanner types into a box. */
const serialsOf = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((serial) => serial.trim())
    .filter(Boolean);

/**
 * The line with these serials. The count follows the serials upward, never
 * down: a box of twelve with three scanned so far is still twelve.
 */
const withSerials = (line: LineDraft, serials: string): LineDraft => {
  const count = serialsOf(serials).length;
  return { ...line, serials, ...(count > Number(line.quantity) ? { quantity: String(count) } : {}) };
};

/** A line is named by what it is once that is typed: "ThinkPad T14", not "Line 2". */
const lineTitle = (line: LineDraft, index: number, bulkLines: StockRow[] = []): string =>
  (line.existingItemId && bulkLines.find((row) => row.itemId === line.existingItemId)?.title) ||
  line.title.trim() ||
  `Item ${index + 1}`;

/** What Receive will make of a line, in a sentence, so the choice above it reads as an outcome. */
function lineSummary(line: LineDraft, bulkLines: StockRow[]): string {
  const quantity = Number(line.quantity);
  if (!Number.isInteger(quantity) || quantity < 1) return "Say how many arrived.";
  const cost = centsFromInput(line.cost);
  const total = cost === null ? "" : `, ${formatMoney(cost * quantity)} in all`;
  if (line.tracking === "bulk") {
    if (line.topUp && !line.existingItemId) return "Pick the line this tops up.";
    const existing = bulkLines.find((row) => row.itemId === line.existingItemId);
    return existing
      ? `Adds ${formatCount(quantity)} to ${existing.title || "that line"}, making ${formatCount(existing.onHand + quantity)} on the shelf${total}.`
      : `One line of ${formatCount(quantity)}${total}.`;
  }
  const scanned = serialsOf(line.serials).length;
  const items = quantity === 1 ? "1 item" : `${formatCount(quantity)} items`;
  const left = quantity - scanned;
  return `${items}, one per unit${total}. ${
    scanned === 0
      ? "No serials yet: scan or type them below, or fill them in later."
      : left > 0
        ? `${scanned} serial${scanned === 1 ? "" : "s"} in, ${left} to fill in later.`
        : "Every serial is in."
  }`;
}

/**
 * Stock coming in: one vendor invoice, many lines. A serialized line becomes
 * one item per unit, each carrying its serial; a bulk line becomes one item
 * with a count, or tops up a bulk line already on the shelf.
 *
 * Serials go in a box, one per line, because that is what a barcode scanner
 * produces: scan, Enter, scan, Enter. The quantity follows the serials once
 * any are typed, so a box of twelve scanned laptops is twelve without
 * counting. Fewer serials than units is allowed: the rest are left blank to
 * fill in later, the same way an item can be saved from photos alone. On a
 * phone, the camera does the scanner's job: each serial it reads is checked
 * by a person, then added as the next line (`ScanSerialDialog`).
 */
export function Receive({ taxonomy }: { taxonomy: Taxonomy }) {
  const [vendor, setVendor] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [receivedOn, setReceivedOn] = useState(todayLocal());
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [lines, setLines] = useState<LineDraft[]>(() => [blankLine()]);
  const [bulkLines, setBulkLines] = useState<StockRow[]>([]);
  const [reserveTags, setReserveTags] = useState<ReserveTag[]>([]);
  const [receipts, setReceipts] = useState<Receipt[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  /** The line whose serials the camera is reading, if any. */
  const [scanningLine, setScanningLine] = useState<number | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      const [list, stock, tags] = await Promise.all([
        api.receipts.list(),
        api.stock.overview(),
        api.reserves.tags(),
      ]);
      setReceipts(list.receipts);
      setReserveTags(tags.reserveTags);
      // A line whose every unit was retired has nothing on the shelf and
      // nothing out: it is retired, and new stock is a new line, not a top-up.
      setBulkLines(
        stock.items.filter((row) => row.tracking === "bulk" && (row.onHand > 0 || row.deployed > 0)),
      );
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load receipts.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const change = (key: number, patch: Partial<LineDraft>) =>
    setLines((now) => now.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  const setSerials = (line: LineDraft, serials: string) => change(line.key, withSerials(line, serials));

  function lineInput(line: LineDraft, index: number): ReceiptLineInput | string {
    const at = lineTitle(line, index, bulkLines);
    const serials = line.tracking === "serialized" ? serialsOf(line.serials) : [];
    const quantity = Number(line.quantity);
    if (!Number.isInteger(quantity) || quantity < 1) return `${at} needs a quantity of at least 1.`;
    if (serials.length > quantity) return `${at} has ${serials.length} serials for ${quantity} units.`;
    if (line.tracking === "bulk" && line.topUp && !line.existingItemId) {
      return `${at}: pick the line to top up, or answer no.`;
    }
    if (!line.title.trim() && !line.existingItemId) return `${at} needs a name.`;
    return {
      title: line.title.trim(),
      tracking: line.tracking,
      quantity,
      unitCostCents: centsFromInput(line.cost),
      categoryId: line.categoryId || null,
      serials,
      existingItemId: line.tracking === "bulk" && line.existingItemId ? line.existingItemId : null,
      tags: line.tags,
    };
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    const built: ReceiptLineInput[] = [];
    for (const [index, line] of lines.entries()) {
      const input = lineInput(line, index);
      if (typeof input === "string") return setError(input);
      built.push(input);
    }

    setBusy(true);
    try {
      const { receipt, itemIds } = await api.receipts.create({
        vendor,
        invoiceNumber,
        receivedOn,
        notes,
        lines: built,
      });
      if (file) {
        try {
          await api.receipts.attach(receipt.id, file);
        } catch (problem) {
          toast(
            problem instanceof ApiError
              ? `Received, but the invoice did not upload: ${problem.message}`
              : "Received, but the invoice did not upload.",
          );
        }
      }
      toast(`Received ${itemIds.length === 1 ? "1 item" : `${itemIds.length} items`}.`);
      setVendor("");
      setInvoiceNumber("");
      setNotes("");
      setFile(null);
      setLines([blankLine()]);
      setFormKey((key) => key + 1);
      await load();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "That receipt did not save.");
    } finally {
      setBusy(false);
    }
  }

  const total = lines.reduce(
    (sum, line) => sum + (centsFromInput(line.cost) ?? 0) * (Number(line.quantity) || 0),
    0,
  );

  return (
    <>
      <PageHeader title="Receive stock" />

      <form key={formKey} className="stack" onSubmit={submit}>
        <Panel title="Invoice" note="The vendor and invoice number stay private, with the cost.">
          <div className="split">
            <label className="field">
              <span className="field-label">Vendor</span>
              <input className="input" value={vendor} onChange={(event) => setVendor(event.target.value)} />
            </label>
            <label className="field">
              <span className="field-label">
                <Icon name="lock" size={12} /> Invoice number
              </span>
              <input
                className="input"
                value={invoiceNumber}
                onChange={(event) => setInvoiceNumber(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">Arrived on</span>
              <input
                className="input"
                type="date"
                required
                value={receivedOn}
                onChange={(event) => setReceivedOn(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field-label">Upload invoice (PDF or photo, optional)</span>
              <input
                className="input input-file"
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </label>
          </div>
          <label className="field">
            <span className="field-label">Notes (optional)</span>
            <input className="input" value={notes} onChange={(event) => setNotes(event.target.value)} />
          </label>
        </Panel>

        {lines.map((line, index) => {
          const scanned = serialsOf(line.serials).length;
          return (
            <Panel
              key={line.key}
              title={lineTitle(line, index, bulkLines)}
              actions={
                lines.length > 1 && (
                  <button
                    type="button"
                    className="icon-button icon-button-sm"
                    aria-label={`Remove ${lineTitle(line, index, bulkLines)}`}
                    onClick={() => setLines((now) => now.filter((one) => one.key !== line.key))}
                  >
                    <Icon name="x" size={16} />
                  </button>
                )
              }
            >
              <div className="stack">
                <div className="field">
                  <span className="field-label">Does each one have its own serial number?</span>
                  <Segmented<StockTracking>
                    label={`${lineTitle(line, index, bulkLines)}: does each one have its own serial number?`}
                    size="sm"
                    value={line.tracking}
                    onChange={(tracking) => change(line.key, { tracking, topUp: false, existingItemId: "" })}
                    options={[
                      { value: "serialized", label: "Yes, each has a serial" },
                      { value: "bulk", label: "No, just count them" },
                    ]}
                  />
                  <span className="muted small">
                    {line.tracking === "serialized"
                      ? "Like laptops: each unit becomes its own item, found by its serial."
                      : "Like cables or chargers: one line with a count."}
                  </span>
                </div>

                {line.tracking === "bulk" && bulkLines.length > 0 && (
                  <div className="field">
                    <span className="field-label">Is this more of something you already stock?</span>
                    <Segmented<"yes" | "no">
                      label={`${lineTitle(line, index, bulkLines)}: is this more of something you already stock?`}
                      size="sm"
                      value={line.topUp ? "yes" : "no"}
                      onChange={(answer) => change(line.key, { topUp: answer === "yes", existingItemId: "" })}
                      options={[
                        { value: "no", label: "No, it is new" },
                        { value: "yes", label: "Yes, top up a line" },
                      ]}
                    />
                    {line.topUp && (
                      <BulkLinePicker
                        lines={bulkLines}
                        value={line.existingItemId}
                        onChange={(existingItemId) => change(line.key, { existingItemId })}
                      />
                    )}
                  </div>
                )}

                {!line.topUp && (
                  <div className="split">
                    {/* The whole row when there is no category beside it: a model name is long. */}
                    <label className={taxonomy.categories.length > 0 ? "field" : "field split-whole"}>
                      <span className="field-label">What it is</span>
                      <input
                        className="input"
                        placeholder={line.tracking === "bulk" ? "65W USB-C charger" : "ThinkPad T14 Gen 5"}
                        value={line.title}
                        onChange={(event) => change(line.key, { title: event.target.value })}
                      />
                    </label>
                    {taxonomy.categories.length > 0 && (
                      <label className="field">
                        <span className="field-label">Category</span>
                        <select
                          className="input"
                          value={line.categoryId}
                          onChange={(event) => change(line.key, { categoryId: event.target.value })}
                        >
                          <option value="">None</option>
                          {taxonomy.categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                )}

                <div className="split">
                  <label className="field">
                    <span className="field-label">How many</span>
                    <input
                      className="input input-number"
                      type="number"
                      min={1}
                      value={line.quantity}
                      onChange={(event) => change(line.key, { quantity: event.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">
                      <Icon name="lock" size={12} /> Cost each
                    </span>
                    <input
                      className="input input-number"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={line.cost}
                      onChange={(event) => change(line.key, { cost: event.target.value })}
                    />
                  </label>
                </div>
                {reserveTags.length > 0 && (
                  <ReserveTagPicker
                    label={
                      line.existingItemId ? "Reserve tags to add to this line" : "Reserve tags for every unit"
                    }
                    lead="Each unit counts toward reserves for"
                    tags={reserveTags}
                    selected={line.tags}
                    onChange={(tags) => change(line.key, { tags })}
                  />
                )}
                {line.tracking === "serialized" && (
                  <div className="field">
                    <label className="field-label" htmlFor={`serials-${line.key}`}>
                      Serials, one per line{scanned > 0 && ` (${scanned} of ${line.quantity})`}
                    </label>
                    <textarea
                      id={`serials-${line.key}`}
                      className="input input-area mono"
                      rows={3}
                      placeholder={"Scan or type, one per line.\nLeave some out to fill in later."}
                      value={line.serials}
                      onChange={(event) => setSerials(line, event.target.value)}
                    />
                    {hasCamera() && (
                      <div>
                        <button
                          type="button"
                          className="button button-ghost button-small"
                          onClick={() => setScanningLine(line.key)}
                        >
                          <Icon name="camera" size={16} />
                          Scan with the camera
                        </button>
                      </div>
                    )}
                    {scanningLine === line.key && (
                      <ScanSerialDialog
                        repeat
                        taken={serialsOf(line.serials)}
                        onUse={(serial) =>
                          // Read the line as it is now: several serials go in before the dialog closes.
                          setLines((now) =>
                            now.map((one) => {
                              if (one.key !== line.key) return one;
                              const serials = [...serialsOf(one.serials), serial].join("\n");
                              return withSerials(one, serials);
                            }),
                          )
                        }
                        onClose={() => setScanningLine(null)}
                      />
                    )}
                  </div>
                )}
                <p className="receive-summary">{lineSummary(line, bulkLines)}</p>
              </div>
            </Panel>
          );
        })}

        {error && <Notice tone="error">{error}</Notice>}

        <div className="panel-foot receive-foot">
          <button
            type="button"
            className="button button-quiet"
            disabled={lines.length >= MAX_RECEIPT_LINES}
            onClick={() => setLines((now) => [...now, blankLine(now[now.length - 1]?.tracking)])}
          >
            <Icon name="plus" size={18} />
            Add another item
          </button>
          <span className="muted">Invoice total: {formatMoney(total)}</span>
          <button type="submit" className="button" disabled={busy}>
            {busy ? "Receiving…" : "Receive"}
          </button>
        </div>
      </form>

      <Panel title="Recent receipts">
        {receipts === null ? (
          <div className="skeleton-line" />
        ) : receipts.length === 0 ? (
          <EmptyState icon="clipboard" title="Nothing received yet." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Arrived</th>
                <th>Vendor</th>
                <th>Invoice</th>
                <th>Items</th>
                <th>Cost</th>
                <th>File</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map((receipt) => (
                <tr key={receipt.id}>
                  <td data-label="Arrived">{calendarDay(receipt.receivedOn)}</td>
                  <td data-label="Vendor">{receipt.vendor || <span className="faint">none</span>}</td>
                  <td data-label="Invoice" className="mono">
                    {receipt.invoiceNumber}
                  </td>
                  <td data-label="Items">{receipt.itemCount}</td>
                  <td data-label="Cost">{formatMoney(receipt.totalCostCents)}</td>
                  <td data-label="File">
                    {receipt.attachment ? (
                      <AttachmentLink attachment={receipt.attachment} />
                    ) : (
                      <ReceiptUpload receiptId={receipt.id} onDone={() => void load()} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </>
  );
}

function ReceiptUpload({ receiptId, onDone }: { receiptId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  return (
    <label className="button button-ghost button-small button-file">
      <Icon name="upload" size={14} />
      {busy ? "Uploading…" : "Add invoice"}
      <input
        type="file"
        className="hidden-input"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (!file) return;
          setBusy(true);
          void api.receipts
            .attach(receiptId, file)
            .then(() => {
              toast("Invoice kept.");
              onDone();
            })
            .catch((problem: unknown) =>
              toast(problem instanceof ApiError ? problem.message : "That did not upload."),
            )
            .finally(() => setBusy(false));
        }}
      />
    </label>
  );
}

/** Past this many lines the picker shows matches for what is typed rather than everything. */
const PICKER_SHOWN = 8;

/**
 * Which bulk line a delivery tops up: a find box and the lines that match,
 * not a select, because a stockroom can have hundreds of lines. With few
 * enough, they are all shown before anything is typed. Once one is picked it
 * stands alone, with Change to pick again.
 */
function BulkLinePicker({
  lines,
  value,
  onChange,
}: {
  lines: StockRow[];
  value: string;
  onChange: (itemId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const picked = lines.find((row) => row.itemId === value);
  const name = (row: StockRow) => row.title || "Untitled";
  const detail = (row: StockRow) =>
    `${formatCount(row.onHand)} on the shelf${row.deployed > 0 ? ` · ${formatCount(row.deployed)} out` : ""}`;

  if (picked) {
    return (
      <div className="line-picked">
        <Icon name="check" size={16} />
        <span className="line-picked-name">
          {name(picked)} <span className="muted small">{detail(picked)}</span>
        </span>
        <button type="button" className="button button-quiet button-small" onClick={() => onChange("")}>
          Change
        </button>
      </div>
    );
  }

  const needle = query.trim().toLowerCase();
  const matches = lines
    .filter((row) => !needle || name(row).toLowerCase().includes(needle))
    .sort((a, b) => name(a).localeCompare(name(b)));
  const shown = needle || lines.length <= PICKER_SHOWN ? matches.slice(0, PICKER_SHOWN) : [];

  return (
    <div className="line-picker">
      <label className="searchbox line-picker-find">
        <Icon name="search" size={16} />
        <input
          aria-label="Find the line to top up"
          placeholder={`Find it among ${formatCount(lines.length)} lines`}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      {shown.length > 0 && (
        <ul className="line-picker-list">
          {shown.map((row) => (
            <li key={row.itemId}>
              <button type="button" className="line-picker-option" onClick={() => onChange(row.itemId)}>
                <span>{name(row)}</span>
                <span className="muted small">{detail(row)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {needle && matches.length === 0 && <p className="muted small">No line is called anything like that.</p>}
      {needle && matches.length > PICKER_SHOWN && (
        <p className="muted small">
          {formatCount(matches.length - PICKER_SHOWN)} more match. Type more of the name.
        </p>
      )}
    </div>
  );
}
